import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { useUnit } from 'effector-react';

import { buildingsApi } from '../api/buildings';
import { heatApi } from '../api/heat';
import { reportsApi } from '../api/reports';
import HeatCollectionProgress, {
  buildProgressFromReadiness,
  buildProgressFromReportSummary,
} from '../components/Heat/HeatCollectionProgress';
import { $canEditComplex, $tenantDataStartDate, $user } from '../store/auth';
import { userHasRole } from '../config/roles';
import {
  buildReportPeriodWindow,
  formatDateOnly,
  formatPeriodRange as formatPeriodRangeSafe,
  localMonthStartDate,
  localYearMonth,
} from '../utils/dates';
import { buildFileName, downloadBlobAsFile } from '../utils/download';

const surfaceSx = {
  borderRadius: 4,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-bg)',
  boxShadow: 'none',
};

const inputSx = {
  '& .MuiInputLabel-root': {
    fontSize: 13,
    color: '#64748B',
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    background: '#F8FAFD',
    borderRadius: 2,
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
    '& fieldset': {
      borderColor: 'rgba(38, 65, 94, 0.16)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(46, 115, 184, 0.48)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2E73B8',
      borderWidth: 1,
    },
  },
};

const autocompletePaperSx = {
  mt: 0.6,
  borderRadius: 2,
  border: '1px solid rgba(38, 65, 94, 0.16)',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    p: 0.5,
    maxHeight: 320,
  },
  '& .MuiAutocomplete-option': {
    minHeight: 34,
    borderRadius: 1.25,
    fontSize: 13,
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(46, 115, 184, 0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(46, 115, 184, 0.08)',
    },
  },
};

const countChipSx = {
  height: 22,
  borderRadius: '999px',
  fontSize: 12,
  fontWeight: 700,
  background: '#F4F7FB',
  color: '#26415E',
  border: '1px solid rgba(38, 65, 94, 0.12)',
};

const tableHeadCellSx = {
  py: 0.9,
  px: 1.15,
  position: 'sticky',
  top: 0,
  zIndex: 3,
  whiteSpace: 'nowrap',
  fontSize: 12,
  fontWeight: 800,
  color: '#526579',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F6F9FD 100%)',
  borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
};

const tableCellSx = {
  py: 0.75,
  px: 1.15,
  fontSize: 13,
  borderColor: 'rgba(15, 23, 42, 0.06)',
  verticalAlign: 'middle',
};

const tableRowHoverSx = {
  cursor: 'pointer',
  transition: 'background-color 0.16s ease, transform 0.16s ease',
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(248, 250, 252, 0.55)',
  },
  '&:hover': {
    backgroundColor: 'rgba(52, 104, 255, 0.04)',
  },
  '& td': {
    ...tableCellSx,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
};

function formatDate(value) {
  return formatDateOnly(value);
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(parsed);
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${part('day')}.${part('month')}.${part('year')} ${part('hour')}:${part('minute')}`;
}

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatNumber(value, digits = 2) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatPeriodRange(start, end, options = {}) {
  return formatPeriodRangeSafe(start, end, options).replace(' — ', ' - ');
}

function dateKey(value) {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(normalized) ? normalized.slice(0, 10) : null;
}

function monthKey(value) {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}/.test(normalized) ? normalized.slice(0, 7) : null;
}

function uniqueApartmentCount(rows) {
  const set = new Set();
  rows.forEach((row) => {
    const key = `${row.corps_name || ''}:${row.apartment_number || ''}`;
    if (row.apartment_number) {
      set.add(key);
    }
  });
  return set.size;
}

function hasPeriodReading(row) {
  return row?.current_value != null || row?.current_ts != null;
}

function deriveStandardBasis(withReadings, withoutReadings, completePairs = withReadings) {
  if (withReadings > 0 && (withoutReadings > 0 || completePairs < withReadings)) {
    return 'Показания и добор пропусков';
  }
  if (withReadings > 0) {
    return 'По показаниям приборов';
  }
  return 'Требует уточнения';
}

function deriveUrtBillingType(period, report) {
  const tariff = period?.tariff ?? report?.summary?.tariff;
  return tariff != null && Number(tariff) > 0 ? 'Рубли' : 'гКал';
}

function formatCalculationBasis(basis, hasData) {
  const raw = String(basis || '').trim();
  const normalized = raw.toUpperCase();
  if (normalized === 'READINGS' || normalized === 'BY_READINGS') return 'По показаниям';
  if (normalized === 'AREA' || normalized === 'BY_AREA') return 'По площади';
  if (/по показаниям/i.test(raw)) return 'По показаниям';
  if (/по площади/i.test(raw)) return 'По площади';
  return hasData ? 'По показаниям' : 'По площади';
}

function formatApiError(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.msg) return item.msg;
        return null;
      })
      .filter(Boolean);
    return parts.length ? parts.join(' ') : fallback;
  }
  if (typeof detail === 'object' && detail.message) return String(detail.message);
  return fallback;
}

function complementShare(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const next = Math.round((100 - num) * 1000) / 1000;
  return String(next);
}

function formatShareScheme(constantShare, variableShare) {
  const constant = constantShare == null ? '—' : `${constantShare}%`;
  const variable = variableShare == null ? '—' : `${variableShare}%`;
  return `пост ${constant} / перем ${variable}`;
}

function formatReportMonthLabel(periodEnd) {
  if (!periodEnd) return '—';
  const parsed = new Date(`${String(periodEnd).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(periodEnd);
  return parsed.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

function reductionBadgeColor(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'default';
  if (num < 0.9) return 'primary';
  if (num < 1) return 'success';
  return 'warning';
}

function createCsvBlob(headers, rows) {
  const escapeCell = (value) => {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const lines = [
    headers.map((item) => escapeCell(item)).join(';'),
    ...rows.map((row) => row.map((item) => escapeCell(item)).join(';')),
  ];

  return new Blob([`\uFEFF${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8;',
  });
}

function summaryChip(label, value) {
  return (
    <Paper key={label} variant="outlined" sx={{ ...surfaceSx, p: 1.15, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function flattenUrtRows(payload) {
  const apartments = payload?.apartments || [];
  const summary = payload?.summary || null;

  return apartments.flatMap((apartment) => {
    const baseRow = {
      apartment_id: apartment.apartment_id,
      apartment_number: apartment.apartment_number || '',
      account_number: apartment.account_number || '',
      corp_name: apartment.corp_name || '',
      area: apartment.area,
      conditional_consumption: apartment.conditional_consumption,
      base_payment: apartment.base_payment,
      constant_payment: apartment.constant_payment,
      constant_part_gcal: apartment.constant_part_gcal,
      variable_payment: apartment.variable_payment,
      variable_part_gcal: apartment.variable_part_gcal,
      delta_payment: apartment.delta_payment,
      total_payment: apartment.total_payment,
      total_gcal: apartment.total_gcal,
      total_area: summary?.total_area,
      data_area: summary?.data_area,
      missing_area: summary?.missing_area,
      tariff: summary?.tariff,
      distribution_scheme: summary
        ? `${summary.variable_share_percent}% / ${summary.constant_share_percent}%`
        : '—',
      reporting_period: summary
        ? `${formatDate(summary.period_start)} - ${formatDate(summary.period_end)}`
        : '—',
      total_house_cost: summary?.total_payments,
      total_house_consumption: summary?.total_house_consumption,
      calculation_basis: apartment.has_data ? 'По показаниям' : 'По площади',
    };

    if (!Array.isArray(apartment.devices) || apartment.devices.length === 0) {
      return [
        {
          ...baseRow,
          room_label: '',
          placement: '',
          coefficient: null,
          reduction_coefficient: null,
          model_name: '',
          serial_number: '',
          previous_value: null,
          current_value: null,
          status: apartment.has_data ? 'OK' : 'Нет показаний',
          raw_delta: null,
          consumption_units: null,
          consumption_gcal: null,
        },
      ];
    }

    return apartment.devices.map((device) => ({
      ...baseRow,
      room_label: device.room_label || '',
      placement: device.placement || '',
      coefficient: device.coefficient,
      reduction_coefficient: device.reduction_coefficient,
      model_name: device.model_name || device.name || '',
      serial_number: device.serial_number || '',
      previous_value: device.previous_value,
      current_value: device.current_value,
      status: device.status || 'OK',
      raw_delta: device.raw_delta,
      consumption_units: device.consumption_units,
      consumption_gcal: device.consumption_gcal,
    }));
  });
}

export default function CalculationsPage() {
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const currentUser = useUnit($user);
  const canEditCalculations = useUnit($canEditComplex);
  const canUnlockCalculations = userHasRole(currentUser, 'admin') || userHasRole(currentUser, 'superadmin');
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [loadingBuildings, setLoadingBuildings] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exportingOverview, setExportingOverview] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('standard');
  const [rows, setRows] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [calculationRevisions, setCalculationRevisions] = useState([]);
  const [archiveActionLoading, setArchiveActionLoading] = useState(false);
  const [rowExportKey, setRowExportKey] = useState('');
  const [focusPeriodId, setFocusPeriodId] = useState('');
  const [focusReadiness, setFocusReadiness] = useState(null);
  const [focusReadinessLoading, setFocusReadinessLoading] = useState(false);
  const [unlockDialog, setUnlockDialog] = useState({ open: false, revision: null, reason: '' });
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [createDialog, setCreateDialog] = useState({
    open: false,
    saving: false,
    formError: '',
    reportMonth: localYearMonth(0),
    houseConsumption: '',
    constantShare: '30',
    variableShare: '70',
    billingMode: 'rubles',
    tariff: '0',
    ignoreReduction: false,
  });
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    row: null,
    loading: false,
    payload: null,
    exportLoading: false,
    focusedApartmentId: null,
  });
  const [loadWarning, setLoadWarning] = useState('');

  useEffect(() => {
    let active = true;

    const loadBuildings = async () => {
      setLoadingBuildings(true);
      try {
        const items = await buildingsApi.getBuildings(0, 500);
        if (!active) return;
        setBuildings(items || []);
        setSelectedBuilding((current) => current || items?.[0] || null);
      } catch (loadError) {
        console.error('load buildings failed', loadError);
        if (active) {
          setError('Не удалось загрузить список домов.');
          setBuildings([]);
        }
      } finally {
        if (active) {
          setLoadingBuildings(false);
        }
      }
    };

    loadBuildings();
    return () => {
      active = false;
    };
  }, []);

  const loadCalculationRevisions = async (building = selectedBuilding) => {
    if (!building?.id) {
      setCalculationRevisions([]);
      return;
    }
    const type = building.heat_allocator_enabled ? 'HEAT_ALLOCATOR' : 'STANDARD';
    try {
      const items = await reportsApi.getCalculationRevisions(String(building.id), type);
      setCalculationRevisions(items);
    } catch (loadError) {
      console.error('load calculation revisions failed', loadError);
      setCalculationRevisions([]);
    }
  };

  useEffect(() => {
    loadCalculationRevisions();
  }, [selectedBuilding?.id, selectedBuilding?.heat_allocator_enabled]);

  const findRowRevision = (row) => {
    const type = row?.mode === 'urt' ? 'HEAT_ALLOCATOR' : 'STANDARD';
    const periodEnd = dateKey(row?.periodEnd || row?.date);
    return calculationRevisions.find(
      (item) => item.calculation_type === type && dateKey(item.period_end) === periodEnd
    ) || null;
  };

  const handleFinalizeCalculation = async () => {
    const row = detailDialog.row;
    if (!row || !selectedBuilding?.id) return;
    setArchiveActionLoading(true);
    setError('');
    try {
      await reportsApi.finalizeCalculation({
        buildingId: String(selectedBuilding.id),
        calculationType: row.mode === 'urt' ? 'HEAT_ALLOCATOR' : 'STANDARD',
        period: row.mode === 'urt' ? null : row.reportParams?.period,
        heatPeriodId: row.mode === 'urt' ? String(row.periodId) : null,
      });
      await loadCalculationRevisions(selectedBuilding);
    } catch (actionError) {
      console.error('finalize calculation failed', actionError);
      setError(actionError?.response?.data?.detail || 'Не удалось закрыть расчёт.');
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const handleUnlockCalculation = async () => {
    const { revision, reason } = unlockDialog;
    if (!revision || reason.trim().length < 3) return;
    setArchiveActionLoading(true);
    try {
      await reportsApi.unlockCalculation(revision.id, reason.trim());
      setUnlockDialog({ open: false, revision: null, reason: '' });
      await loadCalculationRevisions(selectedBuilding);
    } catch (actionError) {
      console.error('unlock calculation failed', actionError);
      setError(actionError?.response?.data?.detail || 'Не удалось создать корректировку.');
    } finally {
      setArchiveActionLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    const loadCalculations = async () => {
      if (!selectedBuilding?.id) {
        setRows([]);
        setLoadWarning('');
        return;
      }

      setLoading(true);
      setError('');
      setLoadWarning('');

      try {
        if (selectedBuilding.heat_allocator_enabled) {
          setMode('urt');
          const periods = await heatApi.listPeriods(String(selectedBuilding.id));
          const startDate = dateKey(tenantDataStartDate);
          const periodItems = Array.isArray(periods)
            ? periods
              .filter((period) => {
                const periodEnd = dateKey(period.period_end);
                return !startDate || !periodEnd || periodEnd >= startDate;
              })
              .slice(0, 12)
            : [];
          const results = await Promise.allSettled(
            periodItems.map(async (period) => {
              const report = await heatApi.buildReport(String(period.id));
              const byReadings = report.apartments.filter((item) => item.has_data).length;
              const byArea = report.apartments.filter((item) => !item.has_data).length;

              const constantShare = period.constant_share_percent ?? report.summary.constant_share_percent;
              const variableShare = period.variable_share_percent ?? report.summary.variable_share_percent;
              return {
                id: period.id,
                mode: 'urt',
                buildingName: selectedBuilding.name,
                date: period.updated_at || period.created_at,
                reportingPeriod: formatReportMonthLabel(period.period_end),
                reportingPeriodRange: formatPeriodRange(period.period_start, period.period_end, { dateOnly: true }),
                opuReading: period.house_consumption_gcal,
                distributionScheme: formatShareScheme(constantShare, variableShare),
                premises: report.apartments.length,
                byReadings,
                byArea,
                calculationType: deriveUrtBillingType(period, report),
                totalCost: report.summary.total_payments,
                tariff: period.tariff ?? report.summary.tariff,
                ignoreReduction: Boolean(period.ignore_reduction_coefficients),
                periodId: period.id,
                periodStart: period.period_start,
                periodEnd: period.period_end,
                // Collection bar: share of apartments with real period readings (not area-fallback).
                coveragePercent:
                  report.apartments.length > 0
                    ? (byReadings / report.apartments.length) * 100
                    : 0,
                coverageOk:
                  report.apartments.length > 0
                    ? byReadings / report.apartments.length >=
                    ((report.summary?.minimum_required_coverage_percent || 50.0001) / 100)
                    : false,
                areaCoveragePercent: report.summary?.readings_coverage_percent,
                summary: report.summary,
              };
            })
          );

          if (!active) return;

          const failedCount = results.filter((item) => item.status === 'rejected').length;
          setLoadWarning(
            failedCount > 0
              ? `Не удалось загрузить ${failedCount} из ${results.length} периодов УРТ.`
              : ''
          );
          setRows(
            results
              .filter((item) => item.status === 'fulfilled')
              .map((item) => item.value)
          );
        } else {
          setMode('standard');
          const rcId = selectedBuilding.residential_complex_id;
          const startDate = dateKey(tenantDataStartDate);
          const startMonth = monthKey(tenantDataStartDate);
          const months = Array.from({ length: 6 }, (_, index) => localMonthStartDate(index)).filter(
            (periodDate) => !startMonth || monthKey(periodDate) >= startMonth
          );
          const results = await Promise.allSettled(
            months.map(async (periodDate) => {
              const report = await reportsApi.getResidentialConsumptionReport({
                residentialComplexId: rcId,
                buildingId: selectedBuilding.id,
                period: periodDate,
              });

              const devicesTotal = report.rows.length;
              // «С показаниями» = есть текущее показание за период, а не только полная пара ok.
              const withReadings = report.rows.filter((row) => hasPeriodReading(row)).length;
              const completePairs = report.rows.filter((row) => row.status === 'ok').length;
              const withoutReadings = devicesTotal - withReadings;
              const premises = uniqueApartmentCount(report.rows);
              const coverage = devicesTotal > 0 ? (withReadings / devicesTotal) * 100 : 0;

              return {
                id: report.period_end,
                mode: 'standard',
                buildingName: selectedBuilding.name,
                date: report.period_end,
                reportingPeriod: formatPeriodRange(report.previous_period_end, report.period_end),
                premises,
                devicesTotal,
                withReadings,
                withoutReadings,
                coverage,
                calculationType: 'Стандартный',
                basis: deriveStandardBasis(withReadings, withoutReadings, completePairs),
                reportParams: {
                  residentialComplexId: rcId,
                  buildingId: selectedBuilding.id,
                  period: periodDate,
                },
              };
            })
          );

          if (!active) return;

          setLoadWarning('');
          setRows(
            results
              .filter((item) => item.status === 'fulfilled')
              .map((item) => item.value)
              .filter((row) => !startDate || !dateKey(row.date) || dateKey(row.date) >= startDate)
          );
        }
      } catch (loadError) {
        console.error('load calculations failed', loadError);
        if (active) {
          setRows([]);
          setLoadWarning('');
          setError('Не удалось загрузить расчёты по выбранному дому.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadCalculations();
    return () => {
      active = false;
    };
  }, [selectedBuilding, tenantDataStartDate, reloadToken]);

  useEffect(() => {
    setFocusPeriodId((current) => {
      if (!rows.length) return '';
      if (mode === 'urt') {
        if (current && rows.some((row) => String(row.periodId) === String(current))) return current;
        return rows[0]?.periodId ? String(rows[0].periodId) : '';
      }
      if (current && rows.some((row) => String(row.id) === String(current))) return current;
      return rows[0]?.id ? String(rows[0].id) : '';
    });
  }, [mode, rows]);

  useEffect(() => {
    let cancelled = false;
    if (mode !== 'urt' || !selectedBuilding?.id || !focusPeriodId) {
      setFocusReadiness(null);
      setFocusReadinessLoading(false);
      return undefined;
    }
    setFocusReadinessLoading(true);
    (async () => {
      try {
        const result = await heatApi.getReadiness(String(selectedBuilding.id), String(focusPeriodId));
        if (!cancelled) setFocusReadiness(result);
      } catch (loadError) {
        if (!cancelled) setFocusReadiness(null);
      } finally {
        if (!cancelled) setFocusReadinessLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, selectedBuilding?.id, focusPeriodId, reloadToken]);

  const latestDate = useMemo(() => {
    if (!rows.length) return null;
    return rows[0]?.date || null;
  }, [rows]);

  const focusedCalculationRow = useMemo(() => {
    if (!focusPeriodId) return null;
    if (mode === 'urt') {
      return rows.find((row) => String(row.periodId) === String(focusPeriodId)) || null;
    }
    return rows.find((row) => String(row.id) === String(focusPeriodId)) || null;
  }, [mode, focusPeriodId, rows]);

  const calculationPeriodOptions = useMemo(
    () =>
      rows.slice(0, 8).map((row) => ({
        id: mode === 'urt' ? String(row.periodId) : String(row.id),
        label: row.reportingPeriod,
      })),
    [rows, mode]
  );

  const focusedCollectionProgress = useMemo(() => {
    if (!focusedCalculationRow) return null;

    if (mode === 'urt') {
      const reportProgress = buildProgressFromReportSummary(focusedCalculationRow.summary);
      const readinessProgress = buildProgressFromReadiness(focusReadiness);
      const premises = Number(focusedCalculationRow.premises) || 0;
      const byReadings = Number(focusedCalculationRow.byReadings) || 0;
      const byArea = Number(focusedCalculationRow.byArea) || 0;
      // Collection progress = apartments that actually have period readings.
      // Do not use area-coverage here: "по площади" apartments inflate it without any collected data.
      const devicesTotal = Number(focusReadiness?.summary?.devices_total);
      const devicesReady = Number(focusReadiness?.summary?.devices_ready);
      const useDevices =
        Number.isFinite(devicesTotal) && devicesTotal > 0 && Number.isFinite(devicesReady);
      const percent = useDevices
        ? (devicesReady / devicesTotal) * 100
        : premises > 0
          ? (byReadings / premises) * 100
          : 0;
      const threshold =
        readinessProgress?.threshold ??
        reportProgress?.threshold ??
        (Number(selectedBuilding?.heat_min_readings_percent) || 50.0001);
      const ok = percent + 1e-9 >= Number(threshold);
      const readyLabel =
        byReadings === 0 && byArea > 0
          ? 'Показания за период ещё не собраны — квартиры пока идут по площади'
          : ok
            ? 'Сбор показаний достаточный'
            : 'Ниже порога — сбор показаний ещё идёт';

      return {
        percent,
        threshold: Number(threshold),
        ok,
        readyLabel,
        chips: [
          {
            key: 'apartments',
            label: `По показаниям: ${byReadings}/${premises}`,
            tone: byReadings > 0 ? 'success' : 'warning',
          },
          byArea > 0
            ? {
              key: 'by-area',
              label: `По площади: ${byArea}`,
              tone: 'warning',
            }
            : null,
          useDevices
            ? {
              key: 'devices',
              label: `УРТ готовы: ${devicesReady}/${devicesTotal}`,
            }
            : null,
          reportProgress?.percent != null
            ? {
              key: 'area-coverage',
              label: `Покрытие площади: ${Number(reportProgress.percent).toLocaleString('ru-RU', {
                maximumFractionDigits: 1,
              })}%`,
            }
            : null,
          ...(readinessProgress?.chips || []).filter((chip) =>
            ['readings', 'coeff', 'area', 'errors', 'warnings'].includes(chip.key)
          ),
        ].filter(Boolean),
        issues: readinessProgress?.issues || [],
      };
    }

    const percent = Number(focusedCalculationRow.coverage) || 0;
    const ok = percent >= 80;
    return {
      percent,
      threshold: 80,
      ok,
      readyLabel: ok
        ? 'Большая часть приборов уже с показаниями'
        : 'Есть приборы без показаний за период',
      chips: [
        {
          key: 'with',
          label: `С показаниями: ${focusedCalculationRow.withReadings}/${focusedCalculationRow.devicesTotal}`,
          tone: ok ? 'success' : 'warning',
        },
        focusedCalculationRow.withoutReadings > 0
          ? {
            key: 'without',
            label: `Без показаний: ${focusedCalculationRow.withoutReadings}`,
            tone: 'warning',
          }
          : null,
        {
          key: 'premises',
          label: `Помещений: ${focusedCalculationRow.premises}`,
        },
      ].filter(Boolean),
      issues: [],
    };
  }, [focusedCalculationRow, focusReadiness, mode, selectedBuilding?.heat_min_readings_percent]);

  const focusedCollectionStats = useMemo(() => {
    if (!focusedCalculationRow) return null;
    if (mode === 'urt') {
      return [
        {
          key: 'by-readings',
          label: 'По показаниям',
          value: String(focusedCalculationRow.byReadings ?? 0),
          hint: `из ${focusedCalculationRow.premises || 0} помещений`,
        },
        {
          key: 'by-area',
          label: 'По площади',
          value: String(focusedCalculationRow.byArea ?? 0),
          hint: focusedCalculationRow.byArea ? 'расчёт без УРТ' : 'все по УРТ',
        },
        {
          key: 'opu',
          label: 'ОДПУ',
          value: formatNumber(focusedCalculationRow.opuReading, 3),
          hint: 'Гкал за период',
        },
      ];
    }
    return [
      {
        key: 'with',
        label: 'С показаниями',
        value: String(focusedCalculationRow.withReadings ?? 0),
        hint: `из ${focusedCalculationRow.devicesTotal || 0} приборов`,
      },
      {
        key: 'without',
        label: 'Без показаний',
        value: String(focusedCalculationRow.withoutReadings ?? 0),
        hint: 'требуют внимания',
      },
      {
        key: 'premises',
        label: 'Помещений',
        value: String(focusedCalculationRow.premises ?? 0),
        hint: focusedCalculationRow.basis || 'стандартный расчёт',
      },
    ];
  }, [focusedCalculationRow, mode]);

  const detailCollectionProgress = useMemo(() => {
    if (detailDialog.row?.mode !== 'urt') return null;
    const reportProgress = buildProgressFromReportSummary(detailDialog.payload?.summary);
    const premises = Number(detailDialog.row.premises) || 0;
    const byReadings = Number(detailDialog.row.byReadings) || 0;
    const byArea = Number(detailDialog.row.byArea) || 0;
    const percent = premises > 0 ? (byReadings / premises) * 100 : 0;
    const threshold =
      reportProgress?.threshold ??
      (Number(selectedBuilding?.heat_min_readings_percent) || 50.0001);
    return {
      percent,
      threshold: Number(threshold),
      ok: percent + 1e-9 >= Number(threshold),
      readyLabel:
        byReadings === 0 && byArea > 0
          ? 'Показания за период ещё не собраны — квартиры пока идут по площади'
          : percent + 1e-9 >= Number(threshold)
            ? 'Сбор показаний достаточный'
            : 'Ниже порога — сбор показаний ещё идёт',
      chips: [
        {
          key: 'apartments',
          label: `По показаниям: ${byReadings}/${premises}`,
          tone: byReadings > 0 ? 'success' : 'warning',
        },
        byArea > 0
          ? { key: 'by-area', label: `По площади: ${byArea}`, tone: 'warning' }
          : null,
        reportProgress?.percent != null
          ? {
            key: 'area-coverage',
            label: `Покрытие площади: ${Number(reportProgress.percent).toLocaleString('ru-RU', {
              maximumFractionDigits: 1,
            })}%`,
          }
          : null,
      ].filter(Boolean),
      issues: [],
    };
  }, [detailDialog.payload, detailDialog.row, selectedBuilding?.heat_min_readings_percent]);

  const urtFlatDetailRows = useMemo(
    () => (detailDialog.row?.mode === 'urt' ? flattenUrtRows(detailDialog.payload) : []),
    [detailDialog.payload, detailDialog.row?.mode]
  );

  const urtApartmentDetailRows = useMemo(() => {
    if (detailDialog.row?.mode !== 'urt') return [];
    return (detailDialog.payload?.apartments || []).map((apartment) => {
      const devices = Array.isArray(apartment.devices) ? apartment.devices : [];
      const collected = devices.filter((device) => device.current_value != null || device.previous_value != null).length;
      const reductions = devices
        .map((device) => device.reduction_coefficient)
        .filter((value) => value != null && !Number.isNaN(Number(value)));
      const reduction = reductions.length
        ? reductions.reduce((sum, value) => sum + Number(value), 0) / reductions.length
        : null;
      return {
        apartment_id: apartment.apartment_id,
        apartment_number: apartment.apartment_number,
        area: apartment.area,
        reduction,
        readingsLabel: `${collected}/${devices.length || 0}`,
        calculationBasis: formatCalculationBasis(apartment.calculation_basis, apartment.has_data),
        calculationReason: apartment.calculation_reason || '',
        total_gcal: apartment.total_gcal,
        total_payment: apartment.total_payment,
      };
    });
  }, [detailDialog.payload, detailDialog.row?.mode]);

  const focusedUrtDeviceRows = useMemo(() => {
    if (!detailDialog.focusedApartmentId) return urtFlatDetailRows;
    return urtFlatDetailRows.filter(
      (item) => String(item.apartment_id) === String(detailDialog.focusedApartmentId)
    );
  }, [detailDialog.focusedApartmentId, urtFlatDetailRows]);

  const handleOpenDetail = async (row) => {
    if (row?.mode === 'urt' && row.periodId) {
      setFocusPeriodId(String(row.periodId));
    }
    setDetailDialog({
      open: true,
      row,
      loading: true,
      payload: null,
      exportLoading: false,
      focusedApartmentId: null,
    });

    try {
      if (row.mode === 'urt') {
        const report = await heatApi.buildReport(String(row.periodId));
        setDetailDialog((current) => ({
          ...current,
          loading: false,
          payload: report,
        }));
      } else {
        const report = await reportsApi.getResidentialConsumptionReport(row.reportParams);
        setDetailDialog((current) => ({
          ...current,
          loading: false,
          payload: report,
        }));
      }
    } catch (detailError) {
      console.error('load calculation detail failed', detailError);
      setDetailDialog((current) => ({
        ...current,
        loading: false,
        payload: null,
      }));
      setError('Не удалось загрузить детальный расчёт.');
    }
  };

  const handleCloseDetail = () => {
    setExportMenuAnchor(null);
    setDetailDialog({
      open: false,
      row: null,
      loading: false,
      payload: null,
      exportLoading: false,
      focusedApartmentId: null,
    });
  };

  const handleExportOverviewCsv = () => {
    if (!rows.length) return;
    setExportingOverview(true);
    try {
      const headers =
        mode === 'urt'
          ? [
            'Дата',
            'Отчетный период',
            'Показания ОДПУ',
            'Схема распределения',
            'Помещений',
            'По показаниям',
            'По площади',
            'Тип расчета',
            'Общие затраты дома, ₽',
            'Тариф, ₽',
          ]
          : [
            'Дата',
            'Отчетный период',
            'Помещений',
            'Приборов',
            'С показаниями',
            'Без показаний',
            'Покрытие, %',
            'Тип расчета',
            'Основа начисления',
          ];

      const csvRows =
        mode === 'urt'
          ? rows.map((row) => [
            formatDate(row.date),
            row.reportingPeriod,
            formatNumber(row.opuReading, 3),
            row.distributionScheme,
            row.premises,
            row.byReadings,
            row.byArea,
            row.calculationType,
            formatMoney(row.totalCost),
            formatMoney(row.tariff),
          ])
          : rows.map((row) => [
            formatDate(row.date),
            row.reportingPeriod,
            row.premises,
            row.devicesTotal,
            row.withReadings,
            row.withoutReadings,
            formatNumber(row.coverage, 1),
            row.calculationType,
            row.basis,
          ]);

      const blob = createCsvBlob(headers, csvRows);
      downloadBlobAsFile(
        blob,
        buildFileName(
          ['calculations', selectedBuilding?.name, mode, new Date().toISOString().slice(0, 10)],
          'calculations.csv'
        )
      );
    } finally {
      setExportingOverview(false);
    }
  };

  const handleExportDetail = async (kind) => {
    if (!detailDialog.row) return;
    setDetailDialog((current) => ({ ...current, exportLoading: true }));
    try {
      if (detailDialog.row.mode === 'urt') {
        if (kind === 'receipts-zip') {
          const blob = await heatApi.downloadReceiptsZip(String(detailDialog.row.periodId));
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['billingservice', detailDialog.row.buildingName, detailDialog.row.periodEnd, 'квитанции'],
              'receipts.zip'
            )
          );
        } else if (kind === 'c300') {
          const blob = await heatApi.downloadReportC300(String(detailDialog.row.periodId));
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['billingservice', 'urt', detailDialog.row.buildingName, detailDialog.row.periodEnd, 'С-300'],
              'urt-c300.csv'
            )
          );
        } else if (kind === 'excel-short' || kind === 'excel-full') {
          const excelFormat = kind === 'excel-short' ? 'short' : 'full';
          const blob = await heatApi.downloadReportExcel(
            String(detailDialog.row.periodId),
            excelFormat,
          );
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['urt', detailDialog.row.buildingName, detailDialog.row.periodStart, detailDialog.row.periodEnd, excelFormat],
              'urt-report.xlsx'
            )
          );
        } else {
          const csvRows = urtFlatDetailRows.map((item) => [
            item.apartment_number,
            item.area,
            item.reduction_coefficient,
            item.conditional_consumption,
            item.base_payment,
            item.constant_payment,
            item.constant_part_gcal,
            item.variable_payment,
            item.variable_part_gcal,
            item.delta_payment,
            item.total_payment,
            item.total_gcal,
            item.room_label || item.placement,
            item.coefficient,
            item.model_name,
            item.serial_number,
            item.previous_value,
            item.current_value,
            item.status,
            item.total_area,
            item.data_area,
            item.missing_area,
            item.tariff,
            item.distribution_scheme,
            item.reporting_period,
            item.total_house_cost,
            item.total_house_consumption,
            item.consumption_units,
            item.consumption_gcal,
            item.calculation_basis,
          ]);
          const blob = createCsvBlob(
            [
              'Помещение',
              'Площадь, м²',
              'Пониж. коэффициент',
              'Сумма пересчит. показаний, ед.',
              'Основной расчет, ₽',
              'Постоянная часть, ₽',
              'Постоянная часть, Гкал',
              'Переменная часть, ₽',
              'Переменная часть, Гкал',
              'Перерасчет, ₽',
              'Итого, ₽',
              'Итого, Гкал',
              'Расположение',
              'Коэфф. ОП',
              'Модель распред.',
              '№ распред.',
              'Нач. показ.',
              'Кон. показ.',
              'Состояние',
              'Общая площадь дома, м²',
              'Площадь с данными, м²',
              'Площадь без данных, м²',
              'Тариф, ₽/Гкал',
              'Схема распределения',
              'Отчетный период',
              'Затраты дома, ₽',
              'Потребление дома, Гкал',
              'Количество пересчитанных единиц',
              'Количество Гкал',
              'Расчет: по площади / по показаниям',
            ],
            csvRows
          );
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['urt', detailDialog.row.buildingName, detailDialog.row.periodStart, detailDialog.row.periodEnd],
              'urt-report.csv'
            )
          );
        }
      } else if (kind === 'excel-full' || kind === 'c300') {
        const exportFormat = kind === 'c300' ? 'c300_csv' : 'xlsx';
        const result = await reportsApi.downloadResidentialConsumptionReport(
          detailDialog.row.reportParams,
          exportFormat,
        );
        downloadBlobAsFile(
          result.data,
          result.fileName ||
          buildFileName(
            ['consumption', detailDialog.row.buildingName, detailDialog.row.date, exportFormat],
            exportFormat === 'c300_csv' ? 'consumption-c300.csv' : 'consumption-report.xlsx'
          )
        );
      } else {
        const payload = detailDialog.payload;
        const csvRows = (payload?.rows || []).map((item) => [
          item.apartment_number || '',
          item.corps_name || '',
          item.system_type || '',
          item.device_name || '',
          item.device_serial || '',
          formatNumber(item.current_value, 3),
          formatDateTime(item.current_ts),
          formatNumber(item.previous_value, 3),
          formatDateTime(item.previous_ts),
          formatNumber(item.consumption, 3),
          item.status_label || item.status || '',
        ]);
        const blob = createCsvBlob(
          [
            'Помещение',
            'Корпус',
            'Тип',
            'Прибор',
            'Серийный номер',
            'Текущее значение',
            'Дата текущего',
            'Предыдущее значение',
            'Дата предыдущего',
            'Расход',
            'Статус',
          ],
          csvRows
        );
        downloadBlobAsFile(
          blob,
          buildFileName(
            ['consumption', detailDialog.row.buildingName, detailDialog.row.date],
            'consumption-report.csv'
          )
        );
      }
    } catch (exportError) {
      console.error('export calculation detail failed', exportError);
      setError('Не удалось выгрузить файл расчёта.');
    } finally {
      setDetailDialog((current) => ({ ...current, exportLoading: false }));
    }
  };

  const handleSelectExportFormat = (kind) => {
    setExportMenuAnchor(null);
    handleExportDetail(kind);
  };

  const handleDownloadApartmentReceipt = async (apartmentId, apartmentNumber, accountNumber) => {
    if (!detailDialog.row?.periodId || !apartmentId) return;
    setDetailDialog((current) => ({ ...current, exportLoading: true }));
    try {
      const blob = await heatApi.downloadApartmentReceipt(
        String(detailDialog.row.periodId),
        String(apartmentId),
        'zip'
      );
      downloadBlobAsFile(
        blob,
        buildFileName(
          [
            'billingservice',
            detailDialog.row.buildingName || detailDialog.row.buildingAddress,
            apartmentNumber || 'кв',
            accountNumber || apartmentNumber || apartmentId,
          ],
          'receipt.zip'
        )
      );
    } catch (exportError) {
      console.error('export apartment receipt failed', exportError);
      setError('Не удалось выгрузить квитанцию по квартире.');
    } finally {
      setDetailDialog((current) => ({ ...current, exportLoading: false }));
    }
  };

  const handleOpenCreateDialog = () => {
    if (!selectedBuilding?.id || mode !== 'urt') return;
    const constantShare =
      selectedBuilding.heat_constant_share_percent != null
        ? String(selectedBuilding.heat_constant_share_percent)
        : '30';
    const variableShare =
      selectedBuilding.heat_variable_share_percent != null
        ? String(selectedBuilding.heat_variable_share_percent)
        : complementShare(constantShare) || '70';
    setCreateDialog({
      open: true,
      saving: false,
      formError: '',
      reportMonth: localYearMonth(0),
      houseConsumption: '',
      constantShare,
      variableShare,
      billingMode:
        selectedBuilding.heat_default_tariff != null && Number(selectedBuilding.heat_default_tariff) > 0
          ? 'rubles'
          : 'gcal',
      tariff:
        selectedBuilding.heat_default_tariff != null
          ? String(selectedBuilding.heat_default_tariff)
          : '0',
      ignoreReduction: false,
    });
  };

  const handleCloseCreateDialog = () => {
    if (createDialog.saving) return;
    setCreateDialog((current) => ({ ...current, open: false, formError: '' }));
  };

  const handleCreateShareChange = (field, rawValue) => {
    setCreateDialog((current) => {
      const next = { ...current, formError: '', [field]: rawValue };
      if (field === 'constantShare') {
        const complement = complementShare(rawValue);
        if (complement !== '') next.variableShare = complement;
      } else if (field === 'variableShare') {
        const complement = complementShare(rawValue);
        if (complement !== '') next.constantShare = complement;
      }
      return next;
    });
  };

  const handleCreateUrtCalculation = async () => {
    if (!selectedBuilding?.id || createDialog.saving) return;

    const constantShare = Number(createDialog.constantShare);
    const variableShare = Number(createDialog.variableShare);
    if (
      !Number.isFinite(constantShare) ||
      !Number.isFinite(variableShare) ||
      constantShare < 0 ||
      variableShare < 0 ||
      Math.abs(constantShare + variableShare - 100) > 0.01
    ) {
      setCreateDialog((current) => ({
        ...current,
        formError: 'Сумма постоянной и переменной частей должна быть равна 100%.',
      }));
      return;
    }

    const houseConsumption = Number(String(createDialog.houseConsumption).replace(',', '.'));
    if (createDialog.houseConsumption === '' || !Number.isFinite(houseConsumption) || houseConsumption < 0) {
      setCreateDialog((current) => ({
        ...current,
        formError: 'Укажите показания ОДПУ (число ≥ 0).',
      }));
      return;
    }

    const periodWindow = buildReportPeriodWindow(
      createDialog.reportMonth,
      selectedBuilding.heat_period_start_day,
      selectedBuilding.heat_period_end_day
    );
    if (!periodWindow.period_start || !periodWindow.period_end) {
      setCreateDialog((current) => ({
        ...current,
        formError: 'Не удалось определить отчётный период.',
      }));
      return;
    }

    let tariffValue = null;
    if (createDialog.billingMode === 'rubles') {
      tariffValue = Number(String(createDialog.tariff).replace(',', '.'));
      if (!Number.isFinite(tariffValue) || tariffValue < 0) {
        setCreateDialog((current) => ({
          ...current,
          formError: 'Укажите корректный тариф в рублях.',
        }));
        return;
      }
    }

    setCreateDialog((current) => ({ ...current, saving: true, formError: '' }));
    setError('');
    try {
      await heatApi.createPeriod(String(selectedBuilding.id), {
        period_start: periodWindow.period_start,
        period_end: periodWindow.period_end,
        house_consumption_gcal: houseConsumption,
        tariff: tariffValue,
        constant_share_percent: constantShare,
        variable_share_percent: variableShare,
        ignore_reduction_coefficients: Boolean(createDialog.ignoreReduction),
        notes: null,
      });
      setCreateDialog((current) => ({ ...current, open: false, saving: false, formError: '' }));
      setReloadToken((value) => value + 1);
      await loadCalculationRevisions(selectedBuilding);
    } catch (createError) {
      console.error('create urt calculation failed', createError);
      setCreateDialog((current) => ({
        ...current,
        saving: false,
        formError: formatApiError(
          createError?.response?.data?.detail,
          'Не удалось создать расчёт УРТ.'
        ),
      }));
    }
  };

  const handleDownloadUrtRowReport = async (row, excelFormat, event) => {
    event?.stopPropagation?.();
    if (!row?.periodId) return;
    const key = `${row.periodId}:${excelFormat}`;
    setRowExportKey(key);
    setError('');
    try {
      const blob = await heatApi.downloadReportExcel(String(row.periodId), excelFormat);
      downloadBlobAsFile(
        blob,
        buildFileName(
          ['urt', row.buildingName, row.periodStart, row.periodEnd, excelFormat],
          'urt-report.xlsx'
        )
      );
    } catch (exportError) {
      console.error('export urt row report failed', exportError);
      setError(
        excelFormat === 'short'
          ? 'Не удалось скачать частичный отчёт.'
          : 'Не удалось скачать полный отчёт.'
      );
    } finally {
      setRowExportKey('');
    }
  };

  const handleDownloadArchivedReport = async (artifact) => {
    if (!artifact?.id) return;
    setArchiveActionLoading(true);
    setError('');
    try {
      const result = await reportsApi.downloadCalculationArtifact(artifact.id);
      downloadBlobAsFile(result.data, result.fileName || artifact.file_name);
    } catch (downloadError) {
      console.error('download archived calculation failed', downloadError);
      setError('Не удалось скачать сохранённый отчёт.');
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const standardDetailRows = detailDialog.payload?.rows || [];
  const currentRevision = findRowRevision(detailDialog.row);

  return (
    <div className="home-container" style={{ marginLeft: 24, marginRight: 24, paddingBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          ...surfaceSx,
          p: { xs: 1, md: 1.15 },
          mb: 1,
          borderRadius: 3,
          background: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={0.8}
          justifyContent="flex-end"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.75} sx={{ width: '100%' }}>
            <Autocomplete
              size="small"
              loading={loadingBuildings}
              options={buildings}
              value={selectedBuilding}
              onChange={(event, value) => setSelectedBuilding(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              slotProps={{ paper: { sx: autocompletePaperSx } }}
              sx={{ flex: { xs: '1 1 auto', md: '0 0 360px' }, ...inputSx }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Дом"
                  placeholder="Выберите дом"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBuildings ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={handleExportOverviewCsv}
              disabled={!rows.length || exportingOverview}
              sx={{ height: 40, borderRadius: 2, px: 2 }}
            >
              CSV
            </Button>
            {mode === 'urt' && canEditCalculations ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<AddRoundedIcon />}
                onClick={handleOpenCreateDialog}
                disabled={!selectedBuilding?.id}
                sx={{ height: 40, borderRadius: 2, px: 2 }}
              >
                Добавить
              </Button>
            ) : null}
            <Chip
              label={mode === 'urt' ? 'Режим: УРТ' : 'Режим: стандартный'}
              size="small"
              sx={{
                ...countChipSx,
                height: 40,
                borderRadius: 2,
                alignSelf: { xs: 'stretch', md: 'center' },
                justifyContent: 'center',
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {typeof error === 'string' ? error : formatApiError(error, 'Произошла ошибка.')}
        </Alert>
      )}
      {loadWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLoadWarning('')}>
          {loadWarning}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
        {[
          summaryChip('Периодов', rows.length),
          summaryChip('Последняя дата', latestDate ? formatDate(latestDate) : '—'),
          summaryChip('Тип дома', mode === 'urt' ? 'С УРТ' : 'Без УРТ'),
        ]}
      </Stack>

      <Box sx={{ mb: 1.25 }}>
        <HeatCollectionProgress
          title="Прогресс сбора данных"
          periodLabel={focusedCalculationRow?.reportingPeriod || ''}
          progress={focusedCollectionProgress}
          loading={Boolean(mode === 'urt' && focusReadinessLoading && !focusedCollectionProgress)}
          emptyText={
            rows.length
              ? 'Выберите расчёт в таблице, чтобы увидеть прогресс сбора.'
              : 'Нет периодов для отображения прогресса.'
          }
          stats={focusedCollectionStats}
          periodOptions={calculationPeriodOptions}
          selectedPeriodId={focusPeriodId}
          onPeriodSelect={setFocusPeriodId}
        />
      </Box>

      <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={0.75}
          sx={{
            px: 1.25,
            py: 0.85,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFCFF 100%)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#526579', fontWeight: 700 }}>
            {selectedBuilding?.name || 'Дом не выбран'}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`Периодов: ${rows.length}`} sx={countChipSx} />
            <Chip
              size="small"
              label={mode === 'urt' ? 'УРТ' : 'Стандартный'}
              sx={countChipSx}
            />
          </Stack>
        </Stack>
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 330px)',
            minHeight: 280,
            overflow: 'auto',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': {
              width: 10,
              height: 10,
            },
            '&::-webkit-scrollbar-track': {
              background: '#F5F7FB',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: 999,
              border: '2px solid #F5F7FB',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94A3B8',
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: mode === 'urt' ? 1420 : 980 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: 140 }}>Дата</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 170 }}>Отчетный период</TableCell>
                {mode === 'urt' ? (
                  <>
                    <TableCell sx={tableHeadCellSx}>Показания ОДПУ</TableCell>
                    <TableCell sx={tableHeadCellSx}>Схема распределения</TableCell>
                    <TableCell sx={tableHeadCellSx}>Помещений</TableCell>
                    <TableCell sx={tableHeadCellSx}>По показаниям</TableCell>
                    <TableCell sx={tableHeadCellSx}>По площади</TableCell>
                    <TableCell sx={tableHeadCellSx}>Тип расчета</TableCell>
                    <TableCell sx={tableHeadCellSx}>Сбор данных</TableCell>
                    <TableCell sx={tableHeadCellSx}>Общие затраты дома, ₽</TableCell>
                    <TableCell sx={tableHeadCellSx}>Тариф, ₽</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, width: 132, textAlign: 'right' }}>
                      Действия
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={tableHeadCellSx}>Помещений</TableCell>
                    <TableCell sx={tableHeadCellSx}>Приборов</TableCell>
                    <TableCell sx={tableHeadCellSx}>С показаниями</TableCell>
                    <TableCell sx={tableHeadCellSx}>Без показаний</TableCell>
                    <TableCell sx={tableHeadCellSx}>Покрытие, %</TableCell>
                    <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                    <TableCell sx={tableHeadCellSx}>Основа</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={mode === 'urt' ? 12 : 9} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={mode === 'urt' ? 12 : 9} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const revision = findRowRevision(row);
                  const archiveLabel = revision?.status === 'FINALIZED'
                    ? `Закрыт, версия ${revision.revision}`
                    : revision?.status === 'CORRECTION'
                      ? `Корректировка ${revision.revision}`
                      : 'Открыт';
                  const shortBusy = rowExportKey === `${row.periodId}:short`;
                  const fullBusy = rowExportKey === `${row.periodId}:full`;
                  const isFocused =
                    mode === 'urt'
                      ? String(row.periodId) === String(focusPeriodId)
                      : String(row.id) === String(focusPeriodId);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isFocused}
                      sx={{
                        ...tableRowHoverSx,
                        ...(isFocused
                          ? {
                            bgcolor: 'rgba(46, 115, 184, 0.06) !important',
                            boxShadow: 'inset 3px 0 0 #2E73B8',
                          }
                          : null),
                      }}
                      onClick={() => {
                        if (mode === 'urt') {
                          setFocusPeriodId(String(row.periodId));
                          return;
                        }
                        setFocusPeriodId(String(row.id));
                        handleOpenDetail(row);
                      }}
                    >
                      <TableCell>{mode === 'urt' ? formatDateTime(row.date) : formatDate(row.date)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.45} alignItems="flex-start">
                          <span>{row.reportingPeriod}</span>
                          {mode !== 'urt' ? (
                            <Chip
                              size="small"
                              label={archiveLabel}
                              color={revision?.status === 'FINALIZED' ? 'success' : revision?.status === 'CORRECTION' ? 'warning' : 'default'}
                              variant={revision ? 'filled' : 'outlined'}
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      {mode === 'urt' ? (
                        <>
                          <TableCell>{formatNumber(row.opuReading, 4)}</TableCell>
                          <TableCell>{row.distributionScheme}</TableCell>
                          <TableCell>{row.premises}</TableCell>
                          <TableCell>{row.byReadings}</TableCell>
                          <TableCell>{row.byArea}</TableCell>
                          <TableCell>{row.calculationType}</TableCell>
                          <TableCell>
                            <Stack spacing={0.4} sx={{ minWidth: 108 }}>
                              <Stack direction="row" spacing={0.6} alignItems="center">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 800,
                                    color: row.coverageOk ? '#047857' : '#B45309',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {formatNumber(row.coveragePercent, 1)}%
                                </Typography>
                                <Chip
                                  size="small"
                                  label={row.coverageOk ? 'ok' : 'сбор'}
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: row.coverageOk
                                      ? 'rgba(16, 185, 129, 0.12)'
                                      : 'rgba(245, 158, 11, 0.14)',
                                    color: row.coverageOk ? '#047857' : '#B45309',
                                  }}
                                />
                              </Stack>
                              <Box
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(15, 23, 42, 0.08)',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.max(0, Math.min(100, Number(row.coveragePercent) || 0))}%`,
                                    height: '100%',
                                    background: row.coverageOk
                                      ? 'linear-gradient(90deg, #10B981, #34D399)'
                                      : 'linear-gradient(90deg, #F59E0B, #FBBF24)',
                                  }}
                                />
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>{formatMoney(row.totalCost)}</TableCell>
                          <TableCell>{formatMoney(row.tariff)}</TableCell>
                          <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                            <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                              <Tooltip title="Подробнее">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenDetail(row)}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Полный отчёт (Excel)">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={Boolean(rowExportKey)}
                                    onClick={(event) => handleDownloadUrtRowReport(row, 'full', event)}
                                  >
                                    {fullBusy ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <DownloadRoundedIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Частичный отчёт (Excel)">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={Boolean(rowExportKey)}
                                    onClick={(event) => handleDownloadUrtRowReport(row, 'short', event)}
                                  >
                                    {shortBusy ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <ArticleOutlinedIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{row.premises}</TableCell>
                          <TableCell>{row.devicesTotal}</TableCell>
                          <TableCell>{row.withReadings}</TableCell>
                          <TableCell>{row.withoutReadings}</TableCell>
                          <TableCell>{formatNumber(row.coverage, 1)}</TableCell>
                          <TableCell>{row.calculationType}</TableCell>
                          <TableCell>{row.basis}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={detailDialog.open} onClose={handleCloseDetail} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {detailDialog.row?.mode === 'urt'
                  ? `Расчет от ${formatDateTime(detailDialog.row?.date)}`
                  : detailDialog.row?.buildingName || 'Расчёт'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {detailDialog.row?.mode === 'urt'
                  ? `Отчетный период: ${detailDialog.row?.reportingPeriod || '—'}`
                  : detailDialog.row?.reportingPeriod || '—'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {currentRevision?.status === 'FINALIZED' && canUnlockCalculations ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryRoundedIcon />}
                  onClick={() => setUnlockDialog({ open: true, revision: currentRevision, reason: '' })}
                  disabled={archiveActionLoading}
                >
                  Создать корректировку
                </Button>
              ) : currentRevision?.status !== 'FINALIZED' && canEditCalculations ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LockRoundedIcon />}
                  onClick={handleFinalizeCalculation}
                  disabled={detailDialog.loading || archiveActionLoading || !detailDialog.payload}
                >
                  Закрыть расчёт
                </Button>
              ) : null}
              {detailDialog.row?.mode === 'urt' && currentRevision?.status !== 'FINALIZED' && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={() => handleExportDetail('receipts-zip')}
                  disabled={detailDialog.loading || detailDialog.exportLoading || !detailDialog.row}
                >
                  Скачать квитанции
                </Button>
              )}
              {currentRevision?.status !== 'FINALIZED' && <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<DownloadRoundedIcon />}
                onClick={(event) => setExportMenuAnchor(event.currentTarget)}
                disabled={detailDialog.loading || detailDialog.exportLoading || !detailDialog.row}
                aria-haspopup="menu"
                aria-expanded={Boolean(exportMenuAnchor)}
              >
                Экспорт
              </Button>}
              {currentRevision?.status !== 'FINALIZED' && <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                {detailDialog.row?.mode === 'urt' && (
                  <MenuItem onClick={() => handleSelectExportFormat('excel-short')}>
                    Частичный Excel
                  </MenuItem>
                )}
                <MenuItem onClick={() => handleSelectExportFormat('excel-full')}>
                  Полный Excel
                </MenuItem>
                <MenuItem
                  onClick={() => handleSelectExportFormat('csv')}
                  disabled={!detailDialog.payload}
                >
                  Обычный CSV
                </MenuItem>
                <MenuItem onClick={() => handleSelectExportFormat('c300')}>
                  CSV для С-300
                </MenuItem>
              </Menu>}
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {currentRevision && (
            <Paper
              variant="outlined"
              sx={{ ...surfaceSx, p: 1.25, mb: 1.5, background: '#F8FAFC' }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {currentRevision.status === 'FINALIZED'
                      ? `Закрытая версия ${currentRevision.revision}`
                      : `Корректировка ${currentRevision.revision}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentRevision.finalized_at
                      ? `Закрыта ${formatDateTime(currentRevision.finalized_at)}`
                      : currentRevision.unlock_reason || 'Расчёт можно изменить и закрыть повторно.'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {(currentRevision.artifacts || []).map((artifact) => (
                    <Button
                      key={artifact.id}
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadRoundedIcon />}
                      onClick={() => handleDownloadArchivedReport(artifact)}
                      disabled={archiveActionLoading}
                      title={artifact.file_name}
                      sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {artifact.file_name}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          )}
          {currentRevision?.status === 'FINALIZED' ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Эта версия закрыта. Данные зафиксированы в сохранённых файлах выше; ниже — текущий
              просмотр расчёта без возможности новой выгрузки.
            </Alert>
          ) : null}
          {detailDialog.loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : detailDialog.row?.mode === 'urt' ? (
            <>
              {detailCollectionProgress ? (
                <Box sx={{ mb: 1.5 }}>
                  <HeatCollectionProgress
                    title="Прогресс сбора данных"
                    periodLabel={detailDialog.row.reportingPeriod || ''}
                    progress={detailCollectionProgress}
                    stats={[
                      {
                        key: 'by-readings',
                        label: 'По показаниям',
                        value: String(detailDialog.row.byReadings ?? 0),
                        hint: `из ${detailDialog.row.premises || 0}`,
                      },
                      {
                        key: 'by-area',
                        label: 'По площади',
                        value: String(detailDialog.row.byArea ?? 0),
                      },
                      {
                        key: 'opu',
                        label: 'ОДПУ',
                        value: formatNumber(detailDialog.row.opuReading, 3),
                        hint: 'Гкал',
                      },
                    ]}
                  />
                </Box>
              ) : null}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                  {[
                    summaryChip(
                      'Минимальное покрытие',
                      `${formatNumber(
                        detailDialog.payload?.summary?.minimum_required_coverage_percent,
                        4
                      )}%`
                    ),
                    summaryChip('Схема распределения', detailDialog.row.distributionScheme),
                    summaryChip(
                      'Учет квартирных коэффициентов',
                      detailDialog.payload?.summary?.ignore_reduction_coefficients ||
                        detailDialog.row.ignoreReduction
                        ? 'нет'
                        : 'да'
                    ),
                  ]}
                </Stack>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                  {[
                    summaryChip(
                      'Расход дома',
                      `${formatNumber(
                        detailDialog.payload?.summary?.total_house_consumption ?? detailDialog.row.opuReading,
                        4
                      )} Гкал`
                    ),
                    summaryChip('Тариф', `${formatMoney(detailDialog.row.tariff)} ₽ / Гкал`),
                    summaryChip('Затраты дома', `${formatMoney(detailDialog.row.totalCost)} ₽`),
                  ]}
                </Stack>
              </Stack>
              <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                <TableContainer
                  sx={{
                    maxHeight: 520,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 10, height: 10 },
                    '&::-webkit-scrollbar-track': { background: '#F5F7FB' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E1',
                      borderRadius: 999,
                      border: '2px solid #F5F7FB',
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...tableHeadCellSx, width: 48 }}>Тип</TableCell>
                        <TableCell sx={tableHeadCellSx}>№ помещения</TableCell>
                        <TableCell sx={tableHeadCellSx}>Пониж. коэффициент</TableCell>
                        <TableCell sx={tableHeadCellSx}>Площадь, м²</TableCell>
                        <TableCell sx={tableHeadCellSx}>Собрано показаний</TableCell>
                        <TableCell sx={tableHeadCellSx}>Расчет</TableCell>
                        <TableCell sx={tableHeadCellSx}>Всего потреблено, гКал</TableCell>
                        <TableCell sx={tableHeadCellSx}>Всего насчитано, ₽</TableCell>
                        <TableCell sx={{ ...tableHeadCellSx, width: 96, textAlign: 'right' }}>
                          Действия
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {urtApartmentDetailRows.map((item) => (
                        <TableRow key={item.apartment_id || item.apartment_number} hover>
                          <TableCell>
                            <HomeOutlinedIcon fontSize="small" sx={{ color: '#64748B' }} />
                          </TableCell>
                          <TableCell>{item.apartment_number || '—'}</TableCell>
                          <TableCell>
                            {item.reduction == null ? (
                              '—'
                            ) : (
                              <Chip
                                size="small"
                                label={formatNumber(item.reduction, 1)}
                                color={reductionBadgeColor(item.reduction)}
                                sx={{ height: 22, fontWeight: 700 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{formatNumber(item.area, 1)}</TableCell>
                          <TableCell>{item.readingsLabel}</TableCell>
                          <TableCell>{item.calculationBasis}</TableCell>
                          <TableCell>{formatNumber(item.total_gcal, 5)}</TableCell>
                          <TableCell>{formatMoney(item.total_payment)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                              <Tooltip title="Квитанция (Excel+PDF)">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={
                                      detailDialog.exportLoading ||
                                      !item.apartment_id ||
                                      currentRevision?.status === 'FINALIZED'
                                    }
                                    onClick={() =>
                                      handleDownloadApartmentReceipt(
                                        item.apartment_id,
                                        item.apartment_number,
                                        item.apartment_number
                                      )
                                    }
                                  >
                                    <DownloadRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title={
                                  item.calculationReason
                                    ? `Причина расчёта: ${item.calculationReason}`
                                    : 'Детали по приборам квартиры'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setDetailDialog((current) => ({
                                        ...current,
                                        focusedApartmentId: item.apartment_id,
                                      }));
                                      window.requestAnimationFrame(() => {
                                        document
                                          .getElementById('urt-device-details')
                                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      });
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Stack
                id="urt-device-details"
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mt: 2.5, mb: 1 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                  Детализация по приборам
                  {detailDialog.focusedApartmentId
                    ? ` · квартира ${urtApartmentDetailRows.find(
                      (item) => String(item.apartment_id) === String(detailDialog.focusedApartmentId)
                    )?.apartment_number || ''
                    }`
                    : ''}
                </Typography>
                {detailDialog.focusedApartmentId ? (
                  <Button
                    size="small"
                    onClick={() =>
                      setDetailDialog((current) => ({ ...current, focusedApartmentId: null }))
                    }
                  >
                    Показать все
                  </Button>
                ) : null}
              </Stack>
              <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                <TableContainer
                  sx={{
                    maxHeight: 360,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 10, height: 10 },
                    '&::-webkit-scrollbar-track': { background: '#F5F7FB' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E1',
                      borderRadius: 999,
                      border: '2px solid #F5F7FB',
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Помещение</TableCell>
                        <TableCell sx={tableHeadCellSx}>Расположение</TableCell>
                        <TableCell sx={tableHeadCellSx}>Модель</TableCell>
                        <TableCell sx={tableHeadCellSx}>№ распред.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Нач. показ.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Кон. показ.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Постоянная, Гкал</TableCell>
                        <TableCell sx={tableHeadCellSx}>Переменная, Гкал</TableCell>
                        <TableCell sx={tableHeadCellSx}>Итого, ₽</TableCell>
                        <TableCell sx={tableHeadCellSx}>Состояние</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {focusedUrtDeviceRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                            Нет приборов для отображения.
                          </TableCell>
                        </TableRow>
                      ) : (
                        focusedUrtDeviceRows.map((item, index) => (
                          <TableRow key={`${item.apartment_number}-${item.serial_number || 'row'}-${index}`} hover>
                            <TableCell>{item.apartment_number || '—'}</TableCell>
                            <TableCell>{item.room_label || item.placement || '—'}</TableCell>
                            <TableCell>{item.model_name || '—'}</TableCell>
                            <TableCell>{item.serial_number || '—'}</TableCell>
                            <TableCell>{formatNumber(item.previous_value, 3)}</TableCell>
                            <TableCell>{formatNumber(item.current_value, 3)}</TableCell>
                            <TableCell>{formatNumber(item.constant_part_gcal, 5)}</TableCell>
                            <TableCell>{formatNumber(item.variable_part_gcal, 5)}</TableCell>
                            <TableCell>{formatMoney(item.total_payment)}</TableCell>
                            <TableCell>{item.status || '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          ) : (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
                {[
                  summaryChip('Приборов', standardDetailRows.length),
                  summaryChip('Помещений', uniqueApartmentCount(standardDetailRows)),
                  summaryChip(
                    'С показаниями',
                    standardDetailRows.filter((item) => hasPeriodReading(item)).length
                  ),
                  summaryChip(
                    'Без показаний',
                    standardDetailRows.filter((item) => !hasPeriodReading(item)).length
                  ),
                ]}
              </Stack>
              <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                <TableContainer
                  sx={{
                    maxHeight: 520,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 10, height: 10 },
                    '&::-webkit-scrollbar-track': { background: '#F5F7FB' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E1',
                      borderRadius: 999,
                      border: '2px solid #F5F7FB',
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Помещение</TableCell>
                        <TableCell sx={tableHeadCellSx}>Корпус</TableCell>
                        <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                        <TableCell sx={tableHeadCellSx}>Прибор</TableCell>
                        <TableCell sx={tableHeadCellSx}>Серийный номер</TableCell>
                        <TableCell sx={tableHeadCellSx}>Текущее</TableCell>
                        <TableCell sx={tableHeadCellSx}>Предыдущее</TableCell>
                        <TableCell sx={tableHeadCellSx}>Расход</TableCell>
                        <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {standardDetailRows.map((item) => (
                        <TableRow key={item.device_id} hover>
                          <TableCell>{item.apartment_number || '—'}</TableCell>
                          <TableCell>{item.corps_name || '—'}</TableCell>
                          <TableCell>{item.system_type || '—'}</TableCell>
                          <TableCell>{item.device_name || '—'}</TableCell>
                          <TableCell>{item.device_serial || '—'}</TableCell>
                          <TableCell>{formatNumber(item.current_value, 3)}</TableCell>
                          <TableCell>{formatNumber(item.previous_value, 3)}</TableCell>
                          <TableCell>{formatNumber(item.consumption, 3)}</TableCell>
                          <TableCell>{item.status_label || item.status || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialog.open}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Добавление нового расчета</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Расчет для дома:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {selectedBuilding?.name || '—'}
            </Box>
          </Typography>
          {createDialog.formError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {createDialog.formError}
            </Alert>
          ) : null}
          <Stack spacing={1.5}>
            <TextField
              label="Отчетный период"
              type="month"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={createDialog.reportMonth}
              onChange={(event) =>
                setCreateDialog((current) => ({
                  ...current,
                  formError: '',
                  reportMonth: event.target.value,
                }))
              }
              helperText={(() => {
                const window = buildReportPeriodWindow(
                  createDialog.reportMonth,
                  selectedBuilding?.heat_period_start_day,
                  selectedBuilding?.heat_period_end_day
                );
                if (!window.period_start || !window.period_end) return ' ';
                return `Окно: ${formatDate(window.period_start)} — ${formatDate(window.period_end)}`;
              })()}
              disabled={createDialog.saving}
            />
            <TextField
              label="Показания ОДПУ"
              size="small"
              fullWidth
              value={createDialog.houseConsumption}
              onChange={(event) =>
                setCreateDialog((current) => ({
                  ...current,
                  formError: '',
                  houseConsumption: event.target.value,
                }))
              }
              disabled={createDialog.saving}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#526579' }}>
              Схема распределения
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Постоянные, %"
                size="small"
                fullWidth
                value={createDialog.constantShare}
                onChange={(event) => handleCreateShareChange('constantShare', event.target.value)}
                disabled={createDialog.saving}
              />
              <TextField
                label="Переменные, %"
                size="small"
                fullWidth
                value={createDialog.variableShare}
                onChange={(event) => handleCreateShareChange('variableShare', event.target.value)}
                disabled={createDialog.saving}
              />
            </Stack>
            <RadioGroup
              value={createDialog.billingMode}
              onChange={(event) =>
                setCreateDialog((current) => ({
                  ...current,
                  formError: '',
                  billingMode: event.target.value,
                }))
              }
            >
              <FormControlLabel
                value="gcal"
                control={<Radio size="small" disabled={createDialog.saving} />}
                label="Считать только в гКал"
              />
              <FormControlLabel
                value="rubles"
                control={<Radio size="small" disabled={createDialog.saving} />}
                label="Перевести в рубли"
              />
            </RadioGroup>
            {createDialog.billingMode === 'rubles' ? (
              <TextField
                label="Тариф, руб."
                size="small"
                fullWidth
                value={createDialog.tariff}
                onChange={(event) =>
                  setCreateDialog((current) => ({
                    ...current,
                    formError: '',
                    tariff: event.target.value,
                  }))
                }
                disabled={createDialog.saving}
              />
            ) : null}
            <FormControlLabel
              control={
                <Checkbox
                  checked={createDialog.ignoreReduction}
                  onChange={(event) =>
                    setCreateDialog((current) => ({
                      ...current,
                      ignoreReduction: event.target.checked,
                    }))
                  }
                  disabled={createDialog.saving}
                />
              }
              label="Не учитывать квартирные коэффициенты"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={createDialog.saving}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreateUrtCalculation}
            disabled={createDialog.saving || !createDialog.reportMonth}
          >
            {createDialog.saving ? 'Создание…' : 'Создать расчет'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unlockDialog.open}
        onClose={() => !archiveActionLoading && setUnlockDialog({ open: false, revision: null, reason: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Создать корректировку</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Закрытая версия и её файлы останутся без изменений. Укажите причину новой версии.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Причина корректировки"
            value={unlockDialog.reason}
            onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnlockDialog({ open: false, revision: null, reason: '' })}
            disabled={archiveActionLoading}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleUnlockCalculation}
            disabled={archiveActionLoading || unlockDialog.reason.trim().length < 3}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
